<!-- LoadingSpinner.vue -->
<template>
  <div
    class="loading-spinner-container"
    :class="{
      'full-screen': fullScreen,
      'scan-loading': !!phase,
      'is-paused': isHidden,
    }"
  >
    <div class="loading-spinner">
      <div v-if="phase" class="scan-document text-primary" aria-hidden="true">
        <i :class="phaseIcon"></i>
        <span
          v-if="!['queued', 'cancelling'].includes(phase)"
          class="scan-line"
        ></span>
      </div>
      <div v-else class="spinner-border text-primary" aria-hidden="true"></div>
      <p
        v-if="message"
        class="loading-message"
        role="status"
        aria-live="polite"
      >
        {{ message
        }}<span v-if="displayPercentage !== null">
          — {{ displayPercentage }}%</span
        >
      </p>
      <div v-if="phase" class="scan-caption-container">
        <Transition name="scan-caption" mode="out-in">
          <p :key="`${phase}-${captionIndex}`" class="scan-caption">
            {{ currentCaption }}
          </p>
        </Transition>
      </div>
      <p v-if="error" class="loading-message" role="alert">{{ error }}</p>
      <div v-if="canCancel || cancelling" class="scan-cancel">
        <template v-if="confirmingCancel && !cancelling">
          <p class="scan-caption mb-3">
            Cancel this scan and delete the new contract and its uploaded files?
          </p>
          <div class="d-flex flex-wrap justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary mb-0"
              @click="confirmingCancel = false"
            >
              Keep scanning
            </button>
            <button
              type="button"
              class="btn btn-danger mb-0"
              @click="$emit('cancel')"
            >
              Delete contract
            </button>
          </div>
        </template>
        <button
          v-else
          type="button"
          class="btn btn-outline-secondary mb-0"
          :disabled="cancelling"
          @click="confirmingCancel = true"
        >
          {{ cancelling ? "Cancelling scan…" : "Cancel scan" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const scanCaptions = {
  queued: [
    "Your contract is uploaded and waiting to be scanned.",
    "We’ll check for properties, then prepare their scope of works.",
  ],
  native: [
    "Scanning pages for property addresses.",
    "Looking for contract and project numbers.",
    "Identifying where each property’s scope begins.",
  ],
  ocr: [
    "Scanning page images for properties and scope tables.",
    "Reading section items, descriptions, units and quantities.",
    "Saving scanned pages ready for scope extraction.",
  ],
  scope: [
    "Extracting section items into each property’s scope of works.",
    "Grouping scope items under their trades and rooms.",
    "Preparing property details and scope documents.",
  ],
  cancelling: [
    "The new contract and its uploaded files will be deleted once scanning stops.",
  ],
};

export default {
  name: "LoadingSpinner",
  emits: ["cancel"],
  props: {
    fullScreen: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: "Loading...",
    },
    percentage: {
      type: Number,
      default: null,
    },
    error: {
      type: String,
      default: "",
    },
    phase: {
      type: String,
      default: "",
    },
    canCancel: { type: Boolean, default: false },
    cancelling: { type: Boolean, default: false },
  },
  data() {
    return {
      captionIndex: 0,
      captionTimer: null,
      motionPreference: null,
      isHidden: false,
      confirmingCancel: false,
    };
  },
  computed: {
    phaseIcon() {
      return (
        {
          queued: "far fa-clock",
          native: "far fa-file-alt",
          ocr: "far fa-file-image",
          scope: "fas fa-list-ul",
          cancelling: "far fa-clock",
        }[this.phase] || "far fa-file-alt"
      );
    },
    currentCaption() {
      const captions = scanCaptions[this.phase] || [];
      return captions[this.captionIndex % captions.length] || "";
    },
    displayPercentage() {
      if (this.percentage === null || !Number.isFinite(this.percentage))
        return null;
      return Math.min(100, Math.max(0, Math.floor(this.percentage)));
    },
  },
  watch: {
    phase() {
      this.captionIndex = 0;
      this.syncCaptionRotation();
    },
  },
  mounted() {
    this.motionPreference = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    this.motionPreference.addEventListener("change", this.syncCaptionRotation);
    document.addEventListener("visibilitychange", this.syncCaptionRotation);
    this.syncCaptionRotation();
  },
  beforeUnmount() {
    clearInterval(this.captionTimer);
    this.motionPreference?.removeEventListener(
      "change",
      this.syncCaptionRotation,
    );
    document.removeEventListener("visibilitychange", this.syncCaptionRotation);
  },
  methods: {
    syncCaptionRotation() {
      clearInterval(this.captionTimer);
      this.isHidden = document.hidden;
      if (
        !scanCaptions[this.phase] ||
        scanCaptions[this.phase].length < 2 ||
        this.isHidden ||
        this.motionPreference?.matches
      )
        return;
      this.captionTimer = setInterval(() => {
        this.captionIndex += 1;
      }, 6500);
    },
  },
};
</script>

<style scoped>
.loading-spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.loading-spinner-container.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  z-index: 9999;
}

.loading-spinner-container.full-screen.scan-loading {
  background-color: #fff;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 38rem;
  padding: 1.5rem;
}

.loading-message {
  margin: 1.25rem 0 0;
  color: #333;
  font-size: 0.9rem;
  text-align: center;
  max-width: 34rem;
  line-height: 1.6;
  font-weight: 600;
}

.scan-document {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4.75rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  overflow: hidden;
  font-size: 1.75rem;
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0.5rem;
  right: 0.5rem;
  height: 2px;
  background: currentColor;
  animation: scan-document 2.8s cubic-bezier(0.45, 0, 0.55, 1) infinite
    alternate;
}

@keyframes scan-document {
  from {
    transform: translateY(8px);
  }
  to {
    transform: translateY(65px);
  }
}

.scan-caption-container {
  width: 100%;
  min-height: 3.75rem;
  margin-top: 0.75rem;
}

.scan-caption {
  margin: 0;
  color: #525866;
  font-size: 0.875rem;
  line-height: 1.6;
  text-align: center;
  text-wrap: balance;
}

.scan-cancel {
  margin-top: 1rem;
  text-align: center;
  width: 100%;
}

.scan-caption-enter-active {
  transition: opacity 220ms ease-out;
}
.scan-caption-leave-active {
  transition: opacity 140ms ease-in;
}
.scan-caption-enter-from,
.scan-caption-leave-to {
  opacity: 0;
}
.is-paused .scan-line,
.is-paused .spinner-border {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  .scan-line {
    display: none;
  }
  .spinner-border {
    animation: none;
  }
  .scan-caption-enter-active,
  .scan-caption-leave-active {
    transition: none;
  }
}
</style>
